import { graphql } from "@repo/graphql-types/gql";
import Left from "@repo/icons/default/Left";
import {
  createFileRoute,
  useRouteContext,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { useState, type JSX } from "react";
import type { UsuariosProfissionaisPlanos } from "@repo/graphql-types/graphql";
import { CpsAlert } from "corpus";
import { useGraphQL, useGraphQLMutation } from "@/hooks/use-graphql";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  isPlanValidForMigration,
  getFilteredSortedPlans,
  type Plan,
  getCurrentPlan,
  canMigrateImmediately,
  getStartOfNextInvoice,
} from "@/lib/plans";
import { CurrentPlanCard } from "@/components/plan/current-plan-card";
import { trackEvent } from "@/lib/tracking";
import { Button } from "@/components/ui/button/button";
import { UnexpectedErrorDrawer } from "@/components/ui/drawer/unexpected-error-drawer";
import { SuccessChangePlanDrawer } from "@/components/plan/success-change-plan-drawer";
import { ChangePlanAlert } from "@/components/plan/change-plan-alert";
import { ChangePlanCheckboxTerms } from "@/components/plan/change-plan-checkbox-terms";
import { ChangePlanSelectionSection } from "@/components/plan/change-plan-selection-section";
import { useMemberInfo } from "@/lib/member";
import { ScheduleMigrationDrawer } from "@/components/schedule/schedule-migration-drawer";
import { formatWithZonedDate, getCurrentDate } from "@/lib/helpers/date";

const ChangePlanPageQuery = graphql(/* GraphQL */ `
  query ChangePlanPageQuery($currentDate: datetime2!) {
    ...CurrentPlanCardFragment
    ...ChangePlanAlertFragment
    usuarioPlano: usuariosProfissionaisPlanos(where: { ativo: { _eq: true } }) {
      plano {
        codPlano
        nome
        valor
        temFidelidade
        nomeExibicao
        descricao
        tipoPlano
        duracaoFidelidadeEmMeses
        carenciaEmMeses
        migravel
      }
      dataFimFidelidade
      dataInicio
    }
    usuarioPlanoFuturo: usuariosProfissionaisPlanos(
      where: {
        dataInicio: { _gt: $currentDate }
        ativo: { _eq: false }
        dataFim: { _is_null: true }
      }
    ) {
      plano {
        nomeExibicao
      }
      dataInicio
    }
    planos(where: { ativo: { _eq: true }, migravel: { _eq: true } }) {
      codPlano
      nome
      valor
      temFidelidade
      nomeExibicao
      descricao
      tipoPlano
      duracaoFidelidadeEmMeses
      carenciaEmMeses
      migravel
    }
    usuariosProfissionais {
      dataValidacao
      diaVencimento
    }
    usuariosEspecialidades {
      codEspecialidade
    }
    planosMigracoes(where: { ativo: { _eq: true } }) {
      codPlanoDestino
      codPlanoOrigem
    }
  }
`);

const UpdateUsuarioPlanoMigracaoMutation = graphql(`
  mutation LivanceApiUsuarioPlanoMigracao($input: LivanceApiUsuarioPlanoMigracaoInput!) {
    LivanceApiUsuarioPlanoMigracao(arg1: $input) {
      codPlano
    }
  }
`);

const ChangePlanPage = (): JSX.Element => {
  const { user, queryClient, flags } = useRouteContext({ from: "__root__" });
  const router = useRouter();
  const searchParams = useSearch({
    from: "/settings/profile/plan/change/",
  });

  const [isChecked, setIsChecked] = useState(false);
  const [checkedPlan, setCheckedPlan] = useState(0);

  const queryResult = useGraphQL(ChangePlanPageQuery, {
    currentDate: getCurrentDate().toISOString(),
  });
  const { data } = queryResult;

  const {
    planos: plans = [],
    usuarioPlano = [],
    usuariosProfissionais = [],
    usuariosEspecialidades = [],
    planosMigracoes = [],
    usuarioPlanoFuturo = [],
  } = data ?? {};

  const hasFuturePlan = usuarioPlanoFuturo.length > 0;
  const futurePlanName = usuarioPlanoFuturo[0]?.plano.nomeExibicao ?? "";
  const futurePlanDate = formatWithZonedDate(
    usuarioPlanoFuturo[0]?.dataInicio ?? new Date(),
    "dd/MM/yyyy",
  );

  const currentPlan = getCurrentPlan(usuarioPlano as UsuariosProfissionaisPlanos[]);

  const { memberRegistrationDate, memberIsPsico } = useMemberInfo(
    usuariosProfissionais,
    usuariosEspecialidades,
  );

  const paymentDueDay = usuariosProfissionais[0]?.diaVencimento ?? 1;

  const memberRegistrationDateCheckingFlag = flags[
    "deve-considerar-plano-mensal-como-permanencia-de-3-meses"
  ]
    ? memberRegistrationDate
    : "";

  const { mutateAsync: updateUsuarioPlanoMigracaoAsync, isPending } = useGraphQLMutation(
    UpdateUsuarioPlanoMigracaoMutation,
  );

  const handlePlanChange = (value: number): void => {
    if (checkedPlan === value) {
      setCheckedPlan(0);
      return;
    }

    trackEvent("Opção de Plano Selecionado", {
      codUsuario: user.codUsuario,
      codPlano: value,
      label: "Alterar Plano",
      page: "/settings/profile/plan/change/",
    });
    setCheckedPlan(value);
  };

  const handleDrawerState = (action: string | null, navigateBack = false): void => {
    if (navigateBack) {
      router.history.go(-2);
      return;
    }

    void router.navigate({
      to: "/settings/profile/plan/change",
      search: { action },
    });
  };

  const handleSuccessfulUpdate = (): void => {
    setIsChecked(false);
    void queryClient.resetQueries({ queryKey: ["MyProfilePageQuery"] });
    handleDrawerState("change-plan-success");
  };

  const handlePlanUpdate = async (): Promise<void> => {
    handleConfirmationDrawer(false);

    await updateUsuarioPlanoMigracaoAsync(
      {
        input: {
          codUsuario: user.codUsuario,
          codPlano: checkedPlan,
        },
      },
      {
        onSuccess: handleSuccessfulUpdate,
        onError: () => handleDrawerState("error"),
      },
    );
  };

  const handleSuccessDrawer = (value: boolean): void => {
    if (value) {
      void router.navigate({
        to: "/settings/profile/plan/change",
        search: { action: "change-plan-success" },
      });
    } else if (searchParams.action === "change-plan-success") {
      setCheckedPlan(0);
      router.history.go(-3);
    }
  };

  const successDrawerMessage = (): string => {
    if (
      !canMigrateImmediately(planosMigracoes, Number(currentPlan.codPlano), checkedPlan)
    ) {
      return `A partir de ${getStartOfNextInvoice(Number(paymentDueDay))}, você estará no novo plano. Verifique sua fatura para eventuais ajustes devido à diferença de valores entre os planos.`;
    }

    return "A partir de agora, você está no novo plano. Verifique sua fatura para eventuais ajustes devido à diferença de valores entre os planos.";
  };

  const handleConfirmationDrawer = (value: boolean): void => {
    if (value) {
      void router.navigate({
        to: "/settings/profile/plan/change",
        search: { action: "change-plan-confirm" },
      });
    } else if (searchParams.action === "change-plan-confirm") {
      router.history.go(-3);
    }
  };

  const handlePlanSelection = (plan: Plan): void => {
    if (disabled(plan)) return;

    handlePlanChange(plan.codPlano ?? 0);
    document.getElementById("change-plan-button")?.scrollIntoView({ behavior: "smooth" });
  };

  const setShowUnexpectedErrorDrawer = (value: boolean): void => {
    if (value) {
      void router.navigate({
        to: "/settings/profile/plan/change",
        search: { action: "error" },
      });
    } else if (searchParams.action === "error") {
      router.history.back();
    }
  };

  const dataFimFidelidade = usuarioPlano[0]?.dataFimFidelidade;

  const disabled = (plan: Plan): boolean => {
    return !isPlanValidForMigration(
      plan,
      planosMigracoes,
      currentPlan,
      dataFimFidelidade,
    );
  };

  const sortedPlans = getFilteredSortedPlans(
    plans,
    currentPlan,
    planosMigracoes,
    dataFimFidelidade,
  );

  const handlePlanChoice = async (): Promise<void> => {
    if (
      canMigrateImmediately(planosMigracoes, Number(currentPlan.codPlano), checkedPlan)
    ) {
      await handlePlanUpdate();
    } else {
      handleConfirmationDrawer(true);
    }
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Alterar Plano" align="center" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col justify-center items-stretch gap-2 py-4">
                <p className="font-sm font-medium text-neutral-600">Plano atual</p>
                <CurrentPlanCard
                  data={data}
                  memberIsPsico={memberIsPsico}
                  memberRegistrationDate={memberRegistrationDate}
                />
                <ChangePlanAlert data={data} />
                {hasFuturePlan ? (
                  <CpsAlert
                    description=""
                    title={`Seu plano será alterado para o ${futurePlanName} em ${futurePlanDate}. Caso solicite uma nova alteração, a atual será cancelada.`}
                    type="info"
                  />
                ) : null}
              </div>

              <ChangePlanSelectionSection
                sortedPlans={sortedPlans}
                checkedPlan={checkedPlan}
                disabled={disabled}
                memberIsPsico={memberIsPsico}
                memberRegistrationDate={memberRegistrationDateCheckingFlag}
                onPlanSelect={handlePlanSelection}
              />

              <ChangePlanCheckboxTerms isChecked={isChecked} onChange={setIsChecked} />

              <Button
                id="change-plan-button"
                fullWidth
                disabled={!isChecked || checkedPlan === 0}
                onClick={() => {
                  void handlePlanChoice();
                }}
                loading={isPending}
              >
                Alterar Plano
              </Button>

              <UnexpectedErrorDrawer
                open={searchParams.action === "error"}
                setOpen={setShowUnexpectedErrorDrawer}
              />
              <SuccessChangePlanDrawer
                open={searchParams.action === "change-plan-success"}
                setOpen={handleSuccessDrawer}
                onClickProfile={() => {
                  router.history.go(-3);
                }}
                description={successDrawerMessage()}
                onClickSchedule={() => void router.navigate({ to: "/calendar" })}
              />
              <ScheduleMigrationDrawer
                open={searchParams.action === "change-plan-confirm"}
                setOpen={handleConfirmationDrawer}
                onClickContinue={() => void handlePlanUpdate()}
                currentPlanName={currentPlan.nomeExibicao}
                newPlanName={
                  sortedPlans.find((p) => p.codPlano === checkedPlan)?.nomeExibicao ?? ""
                }
                date={getStartOfNextInvoice(Number(paymentDueDay))}
              />
            </div>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/profile/plan/change/")({
  component: ChangePlanPage,
});
